import { AlertCircle, Loader2, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { useDebouncedValue } from '~/hooks/use-debounced-value';
import { cn } from '~/utils/cn';

import { useProductFilterOptions } from '../../../hooks/use-product-metadata';
import type {
  AppliedProductFilters,
  ProductFilterDef,
} from '../../../types/product-metadata.types';
import { buildFilterOptionLabelsForKey } from '../../../utils/filter-option-label';

interface Props {
  def: ProductFilterDef;
  connectorCode: string | null;
  connectionId: string | null;
  /** Narrowing context — backend self-excludes this filter's own key. */
  seedFilters: AppliedProductFilters;
  /** Selected sync/revision snapshot to scope options to (null = latest). */
  syncId?: string | null;
  /** Currently-selected values for this filter (from the drawer's draft). */
  selected: string[];
  onToggle: (option: string) => void;
  /** Locked (default) filter — its last selected value can't be unticked. */
  locked?: boolean;
  /** E2E test-id root (the drawer's); derives `${testId}-section-${def.key}-…` and `${testId}-option-${def.key}-${value-slug}`. */
  testId?: string;
}

/** Filter option value → kebab-case test-id segment (raw value also lands on `data-value`). */
function toOptionTestIdSlug(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'empty'
  );
}

/**
 * Lazy, searchable, infinite-scroll option list for a high-cardinality
 * (`paginated`) multi_select filter. Mounts only when its accordion row is
 * expanded; fetches page 1 on mount and appends further pages via an
 * `IntersectionObserver` sentinel as the user scrolls. Search is debounced
 * (500ms) and resets pagination server-side. "Select all" is intentionally
 * omitted — option sets here exceed 50 values.
 */
export function PaginatedFilterOptions({
  def,
  connectorCode,
  connectionId,
  seedFilters,
  syncId = null,
  selected,
  onToggle,
  locked = false,
  testId = 'paginated-filter-options',
}: Props) {
  const { t } = useTranslation('experiments');
  const sectionTestId = `${testId}-section-${def.key}`;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 500);

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useProductFilterOptions({
      connectorCode,
      connectionId,
      filterKey: def.key,
      search: debouncedSearch.trim(),
      appliedFilters: seedFilters,
      syncId,
      enabled: true,
    });

  // Flatten every loaded page into a single de-duplicated option list.
  const options = useMemo(() => {
    const seen = new Set<string>();
    const flat: string[] = [];
    for (const page of data?.pages ?? []) {
      for (const opt of page.options) {
        if (!seen.has(opt)) {
          seen.add(opt);
          flat.push(opt);
        }
      }
    }
    return flat;
  }, [data]);

  // Collision-aware display labels (leaf names, parent-disambiguated on clash).
  // Breadcrumb filters (product type) keep their full path.
  const labels = useMemo(() => buildFilterOptionLabelsForKey(def.key, options), [def.key, options]);

  const total = data?.pages[0]?.pagination.total ?? 0;
  // A locked filter must keep one value, so its only selected option is pinned.
  const pinnedOption = locked && selected.length === 1 ? selected[0] : undefined;

  // Infinite scroll — fetch the next page when the sentinel scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-3 px-6 pt-1 pb-3">
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
        <Input
          data-testid={`${sectionTestId}-search-input`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          aria-label={t('create.step2.filters.optionsSearchPlaceholder', { label: def.label })}
          placeholder={t('create.step2.filters.optionsSearchPlaceholder', { label: def.label })}
          className="pr-8 pl-8"
        />
        {search && (
          <button
            data-testid={`${sectionTestId}-search-clear-button`}
            type="button"
            aria-label={t('create.step2.filters.optionsClearSearch')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2 cursor-pointer"
            onClick={() => {
              setSearch('');
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isLoading && (
        <div
          data-testid={`${sectionTestId}-loading`}
          className="flex items-center justify-center py-6"
        >
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </div>
      )}

      {isError && (
        <div
          data-testid={`${sectionTestId}-error`}
          className="flex flex-col items-center gap-2 py-6 text-center"
        >
          <div className="bg-destructive/10 text-destructive flex size-7 items-center justify-center rounded-full">
            <AlertCircle className="size-4" />
          </div>
          <p className="text-muted-foreground text-sm">{t('create.step2.filters.optionsError')}</p>
          <Button
            data-testid={`${sectionTestId}-retry-button`}
            variant="outline"
            size="sm"
            className="mt-1 cursor-pointer"
            onClick={() => {
              void refetch();
            }}
          >
            {t('create.step2.filters.retry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && options.length === 0 && (
        <p
          data-testid={`${sectionTestId}-no-results`}
          className="text-muted-foreground py-6 text-center text-sm"
        >
          {t('create.step2.filters.optionsNoResults')}
        </p>
      )}

      {!isLoading && !isError && options.length > 0 && (
        <>
          <div
            data-testid={`${sectionTestId}-option-list`}
            className="max-h-64 space-y-2 overflow-y-auto"
          >
            {options.map((opt) => (
              <div
                key={opt}
                className="flex items-center gap-3"
                title={opt === pinnedOption ? t('create.step2.filters.lockedLastValue') : undefined}
              >
                <Checkbox
                  data-testid={`${testId}-option-${def.key}-${toOptionTestIdSlug(opt)}`}
                  data-value={opt}
                  id={`${def.key}-${opt}`}
                  checked={selected.includes(opt)}
                  disabled={opt === pinnedOption}
                  onCheckedChange={() => {
                    onToggle(opt);
                  }}
                />
                <Label
                  data-testid={`${testId}-option-label-${def.key}-${toOptionTestIdSlug(opt)}`}
                  htmlFor={`${def.key}-${opt}`}
                  className={cn(
                    'text-sm font-normal break-words',
                    opt === pinnedOption ? 'cursor-not-allowed' : 'cursor-pointer',
                  )}
                >
                  {/* Display label only; `opt` stays the raw filter value. */}
                  {labels.get(opt) ?? opt}
                </Label>
              </div>
            ))}

            {/* Sentinel — observed to trigger the next page fetch. */}
            <div
              ref={sentinelRef}
              data-testid={`${sectionTestId}-load-more-sentinel`}
              aria-hidden
              className="h-px"
            />

            {isFetchingNextPage && (
              <div
                data-testid={`${sectionTestId}-loading-more`}
                className="text-muted-foreground flex items-center justify-center gap-2 py-2 text-xs"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('create.step2.filters.optionsLoadingMore')}
              </div>
            )}
          </div>

          {total > 0 && (
            <p data-testid={`${sectionTestId}-total`} className="text-muted-foreground text-xs">
              {t('create.step2.filters.optionsCount', { count: total })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
